import { WEB_AB_TESTS_URL, DEFAULT_AB_VARIANT } from "./constants";
import { requestJSON } from "../../utils/promises/library";
import logger from "../../utils/logger/library";
import { getPath } from "../../utils/path/library";
import { getSelection } from "../../hooks/useABTest/utils";
import { isBrowser } from "../../utils/environmentDetection/library";

/**
 * Returns the ABTESTSCONFIG tests object either from the CBC.APP.ANA object if client side
 * or fetching the ab-tests-config.json file when server side.
 * @returns {object | null} ABTESTSCONFIG object
 */
export const getABTestsConfig = async () => {
    let ABTests = null;

    if (isBrowser()) {
        if (window.CBC?.APP?.ANA?.ABTESTSCONFIG) {
            ABTests = window.CBC.APP.ANA.ABTESTSCONFIG;
        }
    } else {
        try {
            ABTests = await requestJSON(WEB_AB_TESTS_URL);
        } catch (error) {
            logger.error(
                `(getABTestsConfig) request failed for ${WEB_AB_TESTS_URL}`,
                error
            );
        }
    }

    if (!ABTests) {
        return null;
    }

    const { tests } = ABTests;
    return tests;
};

/**
 * Because ABTESTSCONFIG has mappings of the following example key-value type
 * { lineup-optimization-british-columbia: {id: 'a-0HJeAGEsFv', scope: {…}, goals: {…}, variants: {…}} }
 * we want to work with all the values of this object, match the test id we got from the cookie, and return
 * the test object we care about.
 *
 * @param {object} abTests - Key-Value AB tests mapping coming from CBC.APP.ANA.ABTESTSCONFIG.tests or directly from the ab-tests-config.json
 * @param {string} testId - AB Variant test id, e.g. a-VBrJyBK44Q
 * @returns {object | undefined} the specific AB test object e.g. {id: 'xyz', scope: {…}, goals: {…}, variants: {…}}
 */
export const getABTestFromTestId = (abTests, testId) => {
    const test = Object.values(abTests).find((abTest) => abTest.id === testId);
    return test;
};

/**
 * Get the ABTest object that matches the page path.
 *
 * @param {object} abTests - Key-Value AB tests mapping coming from CBC.APP.ANA.ABTESTSCONFIG.tests or directly from the ab-tests-config.json
 * @param {string} path - page path without leading slash, e.g. "news/world"
 * @returns {object | undefined} the specific AB test object e.g. {id: 'xyz', scope: {…}, goals: {…}, variants: {…}}
 */
const getABTestFromPath = (abTests, path) => {
    const test = Object.values(abTests).find(
        (abTest) => getPath(abTest.scope.url) === path
    );
    return test;
};

/**
 * Get and format the lineupSwap, given the specific test object from Web AB Tests data
 *
 * @param {string} selection - variant selected, e.g. "A" or "B"
 * @param {boolean} isServerSide
 * @param {object} test - a specific ab test object, of shape {id: 'xyz', scope: {…}, goals: {…}, variants: {…}}
 * @return {object} lineupSwap object with shape { [testScopePath]: {[defaultLineupSlug]: swapLineupSlug} }
 */
export const getLineupSwap = (selection, test) => {
    const defaultLineupSlug = test?.variants[DEFAULT_AB_VARIANT]?.content;
    const swapLineupSlug = test?.variants[selection]?.content;

    if (!defaultLineupSlug || !swapLineupSlug) {
        return null;
    }

    let testScopePath = getPath(test.scope.url);
    testScopePath = testScopePath === "" ? "homepage" : testScopePath;

    const swapObject = {
        [testScopePath]: {
            [defaultLineupSlug]: swapLineupSlug,
        },
    };

    return swapObject;
};

/**
 * Fetch client-side AB selection from Conductrics by testId from path, and return lineupSwap if available
 * @param {string} path of FLP page to load
 * @return {object} lineupSwap object with shape { [testScopePath]: {[defaultLineupSlug]: swapLineupSlug} }
 */
export const fetchLineupVariant = async (path) => {
    // Default lineupSwap value -- reflects pages that do not have AB testing enabled or is assigned variant A
    let lineupSwapObject = {
        [path]: null,
    };
    // For e2e tests to not get stuck on AB tests
    if (isBrowser() && window?.Cypress) {
        return lineupSwapObject;
    }
    const ABTests = await getABTestsConfig();
    if (!ABTests) {
        return lineupSwapObject;
    }

    const test = getABTestFromPath(ABTests, path);
    if (!test) {
        return lineupSwapObject;
    }

    const selection = await getSelection(test.id);

    if (selection && selection !== DEFAULT_AB_VARIANT) {
        const swapObject = getLineupSwap(selection, test);

        if (swapObject) {
            lineupSwapObject = swapObject;
        }
    }

    return lineupSwapObject;
};

/**
 * Utility function that transforms page sections to exclude native ad content
 * @param {Array} sections
 * @returns page sections without native ads
 */
export const filterNativeAds = (sections) => {
    const transformedSections = sections.map((section) => {
        const transformedContent = section.main.content.map(
            (content, contentIndex) => {
                if (contentIndex !== 0) return content;
                if (content.type === "lineup") {
                    const noNativeAdsContent = content.content.filter(
                        (item) => item.type !== "native-ad"
                    );

                    return {
                        ...content,
                        content: noNativeAdsContent,
                    };
                }
                return content;
            }
        );

        return {
            ...section,
            main: {
                ...section.main,
                content: transformedContent,
            },
        };
    });

    return transformedSections;
};

/**
 * Utility function that transforms page sections to include new load more content
 * @param {*} sections current page sections
 * @param {*} loadMore load more content - disableLoadMore, newContent, newSize
 * @param {*} metadata load more metadata - sectionIndex, contentIndex, position
 * @returns page sections that includes new load more section
 */
export const updatePageSections = (sections, loadMore, metadata) => {
    const { disableLoadMore, newContent, newSize } = loadMore;
    const { sectionIndex, contentIndex, position } = metadata;

    return sections.map((section, index) => {
        if (sectionIndex !== index) return section;
        return {
            ...section,
            [position]: {
                ...section[position],
                content: section[position].content.map((content, index) => {
                    if (contentIndex !== index) return content;
                    return {
                        ...content,
                        disableLoadMore,
                        wrapper: {
                            ...content.wrapper,
                            size:
                                newSize ??
                                content.wrapper.size + newContent.length,
                        },
                        content: content.content
                            .map((item) => {
                                const contentItem = { ...item };
                                if (contentItem.focus)
                                    contentItem.focus = false;

                                return contentItem;
                            })
                            .concat(newContent),
                    };
                }),
            },
        };
    });
};
